import { Box, Typography, Stack } from '@mui/material';
import { currencyFormatter } from 'src/utils/currency-formatter';
import { datesForRange } from 'src/utils/rollup';
import brandColors from 'src/utils/brand-colors';
import {
  categoryForAccount,
  seriesForAccount,
  getForecastingIndex,
  renderCell,
  getRetirementAccounts,
  getAccounts,
  balanceForAccount,
  nameForSegment,
  isCheckingAccount,
} from './utils';
import useBudget from '../budget';

export default function useAssetsForecast({
  period,
  accounts = [],
  dateRange,
  userAssets,
  assetSnapshots,
  accountsSnapshots: accSnaps,
  averageEquityGrowthRate,
  averageRealEstateGrowthRate,
  targetRetirementAge,
  averageRetirementEquityWithdrawalRate,
  socialSecurityIncome,
  birthdate,
  averageIncomeGrowthRate,
  privateAssetsValue,
  averagePrivateAssetGrowthRate,
  averageEquityWithdrawalRate,
  cashflow,
  patches,
  income,
}) {
  // if (accounts?.length === 0 || !accounts) throw new Error('Missing accounts');

  // helper functions to get accounts & forecast an account out
  const options = {
    targetRetirementAge,
    averageRetirementEquityWithdrawalRate,
    socialSecurityIncome,
    birthdate,
    averageIncomeGrowthRate,
    privateAssetsValue,
    averagePrivateAssetGrowthRate,
    averageEquityWithdrawalRate,
    assetSnapshots,
    accountsSnapshots: accSnaps,
    averageEquityGrowthRate,
    averageRealEstateGrowthRate,
    patches,
    cashflow,
  };

  const segments = datesForRange(period, dateRange?.startDate, dateRange?.endDate, options);
  const budgets = useBudget({}, period, patches, segments);
  options.budgets = budgets;

  const depositoryAccounts = getAccounts(accounts, ['checking', 'savings', 'cd', 'money market']);
  const investmentAccounts = getAccounts(accounts, ['brokerage', 'investment']);
  const retirementAccounts = getRetirementAccounts(accounts);

  // const cumulativeCashFlow = cashflow?.rows?.find(({ key }) => key === 'cumCashFlow');
  const allAssets = [
    ...userAssets,
    ...depositoryAccounts,
    ...investmentAccounts,
    ...retirementAccounts,
  ].reduce(
    (unique, asset) => (unique.some((item) => item.id === asset.id) ? unique : [...unique, asset]),
    []
  );

  // Determine the primary account
  let primaryAccount = allAssets.find((e) => e.is_primary);
  if (!primaryAccount) {
    // Find the most valuable checking account
    const checkingAccounts = allAssets.filter((account) => isCheckingAccount(account));
    primaryAccount = checkingAccounts.reduce(
      (max, account) => (balanceForAccount(account) > balanceForAccount(max) ? account : max),
      checkingAccounts[0]
    );
  }

  // rows to display in the table, and generally our combined data sources
  const rows = allAssets
    .flatMap((account, index) => ({
      id: account.id,
      name: account.name,
      type: account.type,
      subtype: account.subtype,
      primaryCategory: categoryForAccount(account),
      detailedCategory: account.name,
      amount: balanceForAccount(account),
      isPrimary: account.is_primary || primaryAccount.id === account.id,
      color: brandColors[index % brandColors.length],
    }))
    .map((account) => ({ ...account, ...seriesForAccount(account, period, segments, options) }));

  const historicalSeries = rows.map((account) => ({
    ...account,
    data: segments.map(({ future }, index) =>
      future ? 0 : Object.values(account?.data)?.[index] || 0
    ),
  }));

  const forecastedSeries = rows.map((account) => ({
    ...account,
    data: segments.map(({ future }, index) =>
      !future ? 0 : Object.values(account?.data)?.[index] || 0
    ),
  }));

  const splitIndex = getForecastingIndex(segments);
  // const portfolioGrowth = forecastAccounts(accounts, segments, splitIndex, equityRate);
  const currentValue = rows?.reduce(
    (acc, account) => acc + (Object.values(account?.data)?.[splitIndex] || 0),
    0
  );

  // sum up the forecasted values for each segment
  const fullRangeForecast = segments.reduce((acc, seg) => {
    acc[seg.label] = rows.reduce((sum, account) => sum + (account?.data?.[seg.label] || 0), 0);
    return acc;
  }, {});

  const columns = [
    {
      field: 'primaryCategory',
      headerName: 'Primary Category',
      width: 200,
    },
    {
      field: 'detailedCategory',
      headerName: 'Detailed Category',
      width: 200,
      renderCell: (p) => (
        <Stack direction="row" alignItems="center" padding={0.5}>
          <Box
            sx={{
              backgroundColor: p.row.color,
              width: '25px',
              minWidth: '25px',
              height: '25px',
              borderRadius: '100%',
              mr: 1,
            }}
          />
          <Typography>{p.row.detailedCategory}</Typography>
        </Stack>
      ),
    },
    ...segments.map((segment, index) => ({
      field: segment.label,
      value: segment.value,
      width: 150,
      type: 'number',
      future: segment.future,
      headerClassName: index % 2 === 0 ? 'even' : 'odd',
      headerName: nameForSegment(segment, options.budgets, period),
      valueGetter: (_, row) => row?.data?.[segment.label] ?? 0,
      valueFormatter: (value) => currencyFormatter.format(value),
      renderCell: (params) => renderCell({ period, segment, params, budgets }),
    })),
  ];

  return {
    allAssets,
    budgets,
    rows,
    columns,
    segments,
    forecast: fullRangeForecast,
    historicalSeries,
    forecastedSeries,
    currentValue,
    colorForKey: (key) => rows.find((e) => e.id === key),
  };
}
